import { useState, useEffect } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { supabase } from './lib/supabase'
import Login from './components/auth/Login'
import ETLUploader from './components/admin/ETLUploader'
import DashboardView from './pages/DashboardView'
import { BuilderPage } from './pages/BuilderPage'
import { LandingPage } from './pages/LandingPage'
import { CKANSyncPanel } from './components/admin/CKANSyncPanel'
//import { FloatingNotice } from './components/FloatingNotice'
import logoBMP from './assets/logos/BMP.png'

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

function App() {
  const [view, setView] = useState<'landing' | 'admin' | 'dashboard' | 'builder' | 'ckan'>('landing')
  const [session, setSession] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  // Detectar ruta solicitada (admin vs pública)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)

    // Acceso admin (oculto, solo para administradores)
    if (params.get('admin') === 'true') {
      setView('dashboard') // Vista dashboard del panel admin
      return
    }

    // Vista pública (por defecto)
    setView('landing')
  }, [])

  useEffect(() => {
    // Verificar sesión actual
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setLoading(false)
    })

    // Escuchar cambios en la autenticación
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
    })

    return () => subscription.unsubscribe()
  }, [])

  const handleLogout = async () => {
    await supabase.auth.signOut()
    setSession(null)
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-bmp-green mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    )
  }

  // Vista pública (landing) - NO requiere autenticación
  if (view === 'landing') {
    return (
      <QueryClientProvider client={queryClient}>
        <LandingPage />
        {/*<FloatingNotice />*/}
      </QueryClientProvider>
    )
  }

  // Vistas protegidas (admin, dashboard, builder) - Requieren autenticación
  if (!session) {
    return <Login onLogin={() => {}} />
  }

  return (
    <QueryClientProvider client={queryClient}>
      <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img
                src={logoBMP}
                alt="Bosque Modelo Palencia"
                className="h-12 w-auto object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-bmp-black">
                  MONBIOFOR
                </h1>
                <p className="text-sm text-gray-600">Panel de Administración</p>
              </div>
            </div>
            <nav className="flex gap-4 items-center">
              <button
                onClick={() => setView('dashboard')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  view === 'dashboard'
                    ? 'bg-bmp-green text-white hover:bg-bmp-green-dark'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Dashboard
              </button>
              <button
                onClick={() => setView('landing')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  // @ts-ignore - view can be 'landing' at runtime
                  view === 'landing'
                    ? 'bg-bmp-green text-white hover:bg-bmp-green-dark'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Landing
              </button>
              <button
                onClick={() => setView('admin')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  view === 'admin'
                    ? 'bg-bmp-green text-white hover:bg-bmp-green-dark'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Admin (ETL)
              </button>
              <button
                onClick={() => setView('builder')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  view === 'builder'
                    ? 'bg-bmp-green text-white hover:bg-bmp-green-dark'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Builder
              </button>
              <button
                onClick={() => setView('ckan')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  view === 'ckan'
                    ? 'bg-bmp-green text-white hover:bg-bmp-green-dark'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                CKAN Sync
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 rounded-lg font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                Cerrar Sesión
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {view === 'dashboard' && <DashboardView />}

        {view === 'admin' && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold mb-2">Panel de Administración</h2>
              <p className="text-gray-600">
                Carga los 13 archivos CSV para poblar la base de datos (144,773 registros)
              </p>
            </div>
            <ETLUploader />
          </div>
        )}

        {view === 'builder' && <BuilderPage />}

        {view === 'ckan' && <CKANSyncPanel />}
      </main>

      {/* Footer */}
      <footer className="mt-16 border-t border-gray-200 bg-white">
        <div className="container mx-auto px-4 py-6 text-center text-sm text-gray-600">
          <p className="font-medium text-gray-800">MONBIOFOR</p>
          <p className="mt-1">Bosque Modelo Palencia</p>
          <p className="mt-1 text-xs">Universidad de Valladolid - CESEFOR</p>
          <p className="mt-3 text-xs">
            Contacto: <a href="mailto:secretariatecnica@palenciabosquemodelo.es" className="text-bmp-purple hover:underline">
              secretariatecnica@palenciabosquemodelo.es
            </a>
          </p>
        </div>
      </footer>
      {/*<FloatingNotice />*/}
    </div>
    </QueryClientProvider>
  )
}

export default App
